import type { RequestOptions, StreamOptions, Transport } from '@compass/client';
import { currentUser } from '@utils/user';
import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { toast } from 'sonner';

import { logout, refreshAccessToken } from '@/services/auth.session';
import { shouldRetryRequest } from '@/services/retryPolicy';
import { useStore } from '@/store/store';
import { formatErrorCode, formatErrorCodeWithLabel } from '@/utils/errorCodeFormatter';
import { type ApiErrorWithCode, getErrorCode } from '@/utils/errorCodes';

/**
 * API base URL configuration
 *
 * Proxy mode (REACT_APP_USE_API_PROXY=true):
 *   - Uses /api prefix with environment-specific routing
 *   - REACT_APP_API_ENV values: 'dev', 'qa', 'prod'
 *   - dev → /api/dev/* → https://dev-api.100ms.ai/compass/api/v1/*
 *   - qa → /api/qa/* → https://qa-api.100ms.ai/compass/api/v1/*
 *   - prod → /api/* → https://api.100ms.ai/compass/api/v1/*
 *
 * Legacy mode (REACT_APP_USE_API_PROXY=false or unset):
 *   - Uses full URL for direct CORS requests
 *   - Example: https://qa-api.100ms.ai/compass/api/v1/organizations/my
 */
/**
 * Environment-to-proxy path mapping
 * DRY: Single source of truth for proxy path prefixes
 */
const PROXY_PATH_MAP: Record<string, string> = {
  dev: '/api-dev',
  qa: '/api-qa',
  prod: '/api',
};

const getApiBaseUrl = (): string => {
  const useProxy = process.env.REACT_APP_USE_API_PROXY === 'true';
  const fullApiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/compass/api/v1';
  const apiEnv = process.env.REACT_APP_API_ENV || 'prod';

  if (useProxy) {
    // Proxy mode: use /api prefix with environment-specific routing
    const proxyPrefix = PROXY_PATH_MAP[apiEnv] || PROXY_PATH_MAP.prod;
    const proxyPath = `${proxyPrefix}/compass/api/v1`;

    console.debug('API Config: Proxy mode enabled', { apiEnv, proxyPath, fullApiUrl });
    return proxyPath;
  }

  // Legacy mode: direct CORS requests
  console.debug('API Config: Legacy CORS mode', { fullApiUrl });
  return fullApiUrl;
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Join the API base URL with a path using exactly one slash, mirroring axios's
 * `combineURLs`. Manual `${API_BASE_URL}${path}` concatenation is NOT slash-safe:
 * when `REACT_APP_API_BASE_URL` ends with a trailing slash (e.g. `.../api/v1/`)
 * and the path has a leading slash (`getApiPath` returns `/organizations/...`),
 * the result is `.../api/v1//organizations/...`. Go's router cleans the `//` with
 * a 301 redirect, and browsers re-issue the redirected request as GET — silently
 * downgrading streaming POSTs (e.g. `caserecords/watch`, the availability
 * heartbeat) to GET, which then 404/500. axios-based calls avoid this because the
 * axios client normalizes baseURL + path; manual fetch/SSE URLs must use this.
 */
export const joinApiUrl = (base: string, path: string): string =>
  `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;

const getCurrentPath = () =>
  window.location.pathname + window.location.search + window.location.hash;

/**
 * Default request configuration
 */
const DEFAULT_CONFIG: AxiosRequestConfig = {
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  responseType: 'json',
  maxBodyLength: Infinity,
  maxContentLength: Infinity,
  withCredentials: true, // REQUIRED for HttpOnly cookies to work with CORS
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null, // This prevents adding [] to array parameters
  },
};

/**
 * File upload/download timeout - 10 minutes (600,000ms)
 * Used for large file operations (recordings, transcripts, documents)
 */
export const FILE_UPLOAD_TIMEOUT = 600000;

/**
 * Create the main API client instance
 */
export const apiClient: AxiosInstance = axios.create(DEFAULT_CONFIG);

/**
 * Configure axios-retry for rate limiting (429) and transient server errors with exponential backoff
 * - Retries on 429 Too Many Requests and transient server errors (502, 503, 504)
 * - Does NOT retry on 500 (server bugs) or 501 (not implemented) - these won't recover
 * - Uses Retry-After header if available, otherwise exponential backoff with jitter
 * - Max 3 retries with up to 30 second delay
 */
axiosRetry(apiClient, {
  retries: 3,
  // Retry on 429 (rate limit) or transient server errors only (502 Bad Gateway,
  // 503 Service Unavailable, 504 Gateway Timeout). Webhook API calls are
  // narrower (idempotent methods only, no 429, no dispatcher-down 503): see
  // shouldRetryRequest.
  retryCondition: shouldRetryRequest,
  retryDelay: (retryCount, error) => {
    // Check for Retry-After header (in seconds)
    const retryAfterHeader = error.response?.headers?.['retry-after'];
    if (retryAfterHeader) {
      const retryAfterSeconds = parseInt(retryAfterHeader, 10);
      if (!isNaN(retryAfterSeconds)) {
        return Math.min(retryAfterSeconds * 1000, 30000);
      }
    }
    // Exponential backoff with jitter: 1s, 2s, 4s base + random jitter
    return axiosRetry.exponentialDelay(retryCount, error, 1000);
  },
  onRetry: (retryCount, _error, requestConfig) => {
    console.warn(`Retrying request (${retryCount}/3) for ${requestConfig.url}`);
  },
  onMaxRetryTimesExceeded: (error, retryCount) => {
    console.error(`Max retries (${retryCount}) exceeded for ${error.config?.url}`);
    toast.error('Server is busy. Please try again in a few moments.');
  },
});

let logoutInProgress = false;
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void }> = [];

/**
 * Transform API paths for test mode.
 * Sandbox routes are mounted at /sandbox (outside /api/v1), so we use relative
 * path traversal to escape the /api/v1 base path.
 */
const transformPathForTestMode = (url: string): string => {
  // Define path transformations for test mode
  // Use ../../sandbox to go up two levels from /api/v1 to root, then into /sandbox
  const transformations = [
    // Organization and program-specific case record endpoints
    {
      pattern: /^\/organizations\/([^/]+)\/programs\/([^/]+)\/caserecords\/search/,
      replacement: '/../../sandbox/organizations/$1/programs/$2/caserecords/search',
    },
    {
      pattern: /^\/organizations\/([^/]+)\/programs\/([^/]+)\/caserecords\/counts/,
      replacement: '/../../sandbox/organizations/$1/programs/$2/caserecords/counts',
    },
    {
      pattern: /^\/organizations\/([^/]+)\/programs\/([^/]+)\/caserecords\/([^/]+)/,
      replacement: '/../../sandbox/organizations/$1/programs/$2/caserecords/$3',
    },
    {
      pattern: /^\/organizations\/([^/]+)\/programs\/([^/]+)\/caserecords$/,
      replacement: '/../../sandbox/organizations/$1/programs/$2/caserecords',
    },
    // Organization-specific assigned case records
    {
      pattern: /^\/organizations\/([^/]+)\/caserecords\/assigned/,
      replacement: '/../../sandbox/organizations/$1/caserecords/assigned',
    },
    // Legacy endpoints (backward compatibility)
    { pattern: /^\/caserecords/, replacement: '/../../sandbox/caserecords' },
    // Workflow case records endpoint
    {
      pattern: /^\/workflows\/([^/]+)\/caserecords/,
      replacement: '/../../sandbox/workflows/$1/caserecords',
    },
  ];

  for (const { pattern, replacement } of transformations) {
    if (pattern.test(url)) {
      const transformedUrl = url.replace(pattern, replacement);
      console.debug(`Test mode: transforming ${url} to ${transformedUrl}`);
      return transformedUrl;
    }
  }

  return url;
};

/**
 * Request interceptor for authentication and organization context
 */
apiClient.interceptors.request.use(
  config => {
    const user = currentUser();
    if (user?.access_token) {
      config.headers.Authorization = `Bearer ${user.access_token}`;
    }

    // Add organization ID header if available
    const selectedOrganizationId = useStore.getState().selectedOrganizationId;
    if (selectedOrganizationId) {
      config.headers['x-org-id'] = selectedOrganizationId;
    }

    // Check if test mode is enabled and transform paths
    const testModeEnabled = useStore.getState().testModeEnabled;
    if (testModeEnabled && config.url) {
      // Only transform relative URLs (not full URLs with domain)
      if (!config.url.startsWith('http://') && !config.url.startsWith('https://')) {
        config.url = transformPathForTestMode(config.url);
      }
    }

    console.debug('API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  error => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  },
);

/**
 * Handle network-related errors
 */
const handleNetworkError = (error: AxiosError): boolean => {
  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !navigator.onLine) {
    console.error('Network error:', error.config?.url);
    return true;
  }
  return false;
};

/**
 * Process queued requests after token refresh
 */
const processQueue = (error: any = null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

/**
 * Retry request with refreshed token
 */
const retryWithToken = (error?: AxiosError) => {
  if (!error?.config) return null;
  const user = currentUser();
  if (user?.access_token) {
    error.config.headers.Authorization = `Bearer ${user.access_token}`;
  }
  return apiClient.request(error.config);
};

/**
 * Handle logout on refresh failure
 */
const handleRefreshFailure = async () => {
  if (logoutInProgress) return;

  logoutInProgress = true;
  await logout();
  logoutInProgress = false;

  toast.error('Session Expired: Please login again.');
  window.location.href = `/login?redirect=${encodeURIComponent(getCurrentPath())}`;
};

/**
 * Attempt to refresh access token
 */
const attemptTokenRefresh = async (error?: AxiosError) => {
  isRefreshing = true;
  console.debug('Attempting token refresh...');

  try {
    const refreshResponse = await refreshAccessToken();

    if (refreshResponse && refreshResponse.access_token) {
      console.debug('Token refreshed successfully');
      isRefreshing = false;
      processQueue();
      return retryWithToken(error);
    }
    throw new Error('Token refresh returned no access token');
  } catch (refreshError) {
    console.error('Token refresh failed:', refreshError);
    isRefreshing = false;
    processQueue(refreshError);
    await handleRefreshFailure();
    return null;
  }
};

/**
 * Queue request while token refresh is in progress
 */
const queueRequest = (error?: AxiosError) => {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  })
    .then(() => retryWithToken(error) || Promise.reject(new Error('No request config')))
    .catch(err => Promise.reject(err));
};

/**
 * Handle 401 Unauthorized errors
 */
const handle401Error = async (url?: string, error?: AxiosError): Promise<any> => {
  console.debug('401 Unauthorized:', url);

  // Don't handle 401 on auth endpoints
  if (
    url?.includes('/auth/login') ||
    url?.includes('/auth/refresh') ||
    url?.includes('/auth/update-password') ||
    logoutInProgress
  ) {
    return null;
  }

  // Refresh token or queue request
  if (!isRefreshing) {
    return attemptTokenRefresh(error);
  }
  return queueRequest(error);
};

/**
 * Handle HTTP status errors
 * Note: 429 (rate limiting) is handled by axios-retry configured above
 */
const handleStatusError = async (error?: AxiosError): Promise<any> => {
  switch (error?.response?.status) {
    case 401:
      return handle401Error(error?.config?.url, error);

    case 403:
      toast.error('Access Denied: You do not have required permission.');
      return new Promise(() => {});

    case 404:
      console.warn('404 Not Found');
      return null;

    default:
      return null;
  }
};

/**
 * Log API error to console (401, 404, and NETWORK_ERROR as warnings, others as errors)
 */
const logApiError = (code: string, status: number | undefined, url: string) => {
  if (status === 401 || status === 404 || code === 'NETWORK_ERROR') {
    console.warn(`API Warning [${code}]:`, status || 'Network Error', url);
  } else {
    console.error(`API Error [${code}]:`, status || 'Network Error', url);
  }
};

/**
 * Enrich error with error code and user-friendly message
 */
const enrichErrorWithCode = (error: AxiosError): ApiErrorWithCode => {
  const errorWithCode = error as ApiErrorWithCode;

  if (error.config?.url && error.config?.method) {
    const status = error.response?.status;

    // Use NETWORK_ERROR code if there's no response (network failure)
    const code =
      status === undefined ? 'NETWORK_ERROR' : getErrorCode(error.config.url, error.config.method);

    errorWithCode.code = code;
    errorWithCode.APIHttpStatus = status;

    const formattedCode = formatErrorCode(code, status);
    errorWithCode.userMessage = formatErrorCodeWithLabel(formattedCode);

    // Log the error appropriately
    logApiError(code, status, error.config.url);
  }

  return errorWithCode;
};

/**
 * Response interceptor for error handling
 */
apiClient.interceptors.response.use(
  response => {
    console.debug(
      'API Response:',
      response.config.method?.toUpperCase(),
      response.config.url,
      response.status,
    );
    return response;
  },
  async (error: AxiosError) => {
    // Enrich error with code and operation
    const errorWithCode = enrichErrorWithCode(error);

    // Handle network errors first
    if (handleNetworkError(error)) {
      return Promise.reject(errorWithCode);
    }

    // Handle HTTP status errors
    const statusPromise = await handleStatusError(error);
    if (statusPromise !== null) {
      return statusPromise;
    }

    return Promise.reject(errorWithCode);
  },
);

/**
 * Create a new API client instance with custom configuration
 */
export const createApiClient = (config?: AxiosRequestConfig): AxiosInstance => {
  return axios.create({
    ...DEFAULT_CONFIG,
    ...config,
    headers: {
      ...DEFAULT_CONFIG.headers,
      ...config?.headers,
    },
  });
};

/**
 * Get HTTP path from API method definition and replace path parameters.
 *
 * This function:
 * 1. Extracts the HTTP path template from api-permissions.json
 * 2. Identifies path parameters (e.g., {user_id}, {role_id})
 * 3. Replaces them with actual values from the pathParams object
 *
 * Query parameters should be passed directly to apiClient.get/post/delete as usual.
 *
 * @template T - Type of the path parameters object
 * @param apiMethod - The API method constant (e.g., API_METHODS.UserRoleService.AddUserRole)
 * @param pathParams - Object containing path parameter values (optional)
 * @returns The path with parameters replaced (e.g., '/users/123/roles')
 * @throws Error if the method is not found in api-permissions.json
 *
 * @example Path parameters only
 * ```typescript
 * import { getApiPath } from '@/services/api.config';
 * import { API_METHODS } from '@/hooks/usePermissions';
 *
 * // For path: /users/{user_id}/roles
 * const url = getApiPath(API_METHODS.UserRoleService.AddUserRole, { user_id: '123' });
 * // Returns: '/users/123/roles'
 * const response = await apiClient.post(url, body);
 * ```
 *
 * @example Path parameters with query params in apiClient
 * ```typescript
 * // For path: /users/{user_id}/roles
 * const url = getApiPath(API_METHODS.UserRoleService.ListUserRoles, { user_id: '123' });
 * // Returns: '/users/123/roles'
 * const response = await apiClient.get(url, {
 *   params: { limit: 10, organization_id: 'org-1' }
 * });
 * // Final URL: /users/123/roles?limit=10&organization_id=org-1
 * ```
 *
 * @example No path parameters
 * ```typescript
 * // For path: /users/me/roles
 * const url = getApiPath(API_METHODS.UserRoleService.GetMyRoles);
 * // Returns: '/users/me/roles'
 * const response = await apiClient.get(url, { params: request });
 * ```
 */
// Re-exported from @compass/client rather than reimplemented: the generated services
// resolve their own paths with the package's copy, and a second implementation here is
// exactly the kind of drift BV-1874 removes. Existing
// `import { getApiPath } from '@/services/api.config'` callers are unaffected.
export { getApiPath } from '@compass/client';

/**
 * SSE Event data structure
 */
interface SSEEvent<T = unknown> {
  data: T;
  event?: string;
  id?: string;
}

/**
 * @deprecated Use `RequestOptions` from `@compass/client` instead.
 *
 * This was `Omit<AxiosRequestConfig, ...>`. The generated services now take the
 * transport-agnostic `RequestOptions`, so an axios-shaped type is no longer assignable to
 * them. Kept as an alias so existing callers (currently only `utils/caseActions.ts`) keep
 * compiling; drop it once those are updated. [BV-1874]
 */
export type ServiceOptions = RequestOptions;

/**
 * SSE connection options
 */
interface SSEOptions {
  /** Called when connection opens */
  onOpen?: () => void;
  /** Called on connection error */
  onError?: (error: Error) => void;
  /** Called when connection closes */
  onClose?: () => void;
  /** Custom headers (authorization is added automatically) */
  headers?: Record<string, string>;

  signal?: AbortSignal;
}

/**
 * Build SSE request headers with authentication
 */
function buildSSEHeaders(customHeaders: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    'Cache-Control': 'no-cache',
    ...customHeaders,
  };

  const user = currentUser();
  if (user?.access_token) {
    headers['Authorization'] = `Bearer ${user.access_token}`;
  }

  const selectedOrganizationId = useStore.getState().selectedOrganizationId;
  if (selectedOrganizationId) {
    headers['x-org-id'] = selectedOrganizationId;
  }

  return headers;
}

/**
 * Parse SSE field value and update event object
 */
function parseSSEField<T>(event: SSEEvent<T>, field: string, value: string): void {
  if (field === 'data') {
    try {
      event.data = JSON.parse(value);
    } catch {
      event.data = value as T;
    }
  } else if (field === 'event') {
    event.event = value;
  } else if (field === 'id') {
    event.id = value;
  }
}

/**
 * Parse a single SSE event text into an SSEEvent object
 */
function parseSSEEvent<T>(eventText: string): SSEEvent<T> | null {
  if (!eventText.trim()) return null;

  const event: SSEEvent<T> = { data: {} as T };
  const lines = eventText.split('\n');

  for (const line of lines) {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;

    const field = line.slice(0, colonIndex).trim();
    const value = line.slice(colonIndex + 1).trim();
    parseSSEField(event, field, value);
  }

  return event;
}

/**
 * Handle SSE connection errors
 */
function handleSSEError(error: unknown, onError?: (error: Error) => void): void {
  if (error instanceof Error && error.name === 'AbortError') {
    console.debug('SSE connection aborted');
    return;
  }
  console.error('SSE connection error:', error);
  onError?.(error instanceof Error ? error : new Error(String(error)));
}

/**
 * Read and yield SSE events from a response stream
 */
async function* readSSEStream<T>(
  reader: ReadableStreamDefaultReader<Uint8Array>,
): AsyncGenerator<SSEEvent<T>, void, unknown> {
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const eventTexts = buffer.split('\n\n');
      buffer = eventTexts.pop() || '';

      for (const eventText of eventTexts) {
        const event = parseSSEEvent<T>(eventText);
        if (event) yield event;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Create a Server-Sent Events (SSE) connection using fetch API.
 *
 * This function establishes an SSE connection to a streaming endpoint and
 * yields parsed events as they arrive. It handles authentication, reconnection,
 * and proper cleanup.
 *
 * @template T - The type of data expected in each SSE event
 * @param url - The SSE endpoint URL (relative to base URL)
 * @param body - The request body to POST
 * @param options - SSE connection options
 * @returns An async generator that yields SSE events
 *
 * @example
 * ```typescript
 * const url = getApiPath(API_METHODS.CaseRecordsService.WatchCaseRecords, {
 *   organization_id,
 *   program_id,
 * });
 *
 * const abortController = new AbortController();
 *
 * try {
 *   for await (const event of createSSEConnection<WatchCaseRecordsResponse>(
 *     url,
 *     { case_record_ids: ['id1', 'id2'] },
 *     { signal: abortController.signal }
 *   )) {
 *     console.log('Received event:', event.data);
 *   }
 * } catch (error) {
 *   console.error('SSE error:', error);
 * }
 *
 * // To close the connection:
 * abortController.abort();
 * ```
 */
async function* createSSEConnection<T>(
  url: string,
  body: Record<string, unknown>,
  options: SSEOptions = {},
): AsyncGenerator<SSEEvent<T>, void, unknown> {
  const { onOpen, onError, onClose, headers = {}, signal } = options;
  const fullUrl = joinApiUrl(API_BASE_URL, url);

  try {
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: buildSSEHeaders(headers),
      body: JSON.stringify(body),
      credentials: 'include',
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `SSE connection failed: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    if (!response.body) {
      throw new Error('SSE response body is null');
    }

    onOpen?.();

    const reader = response.body.getReader();
    yield* readSSEStream<T>(reader);
  } catch (error) {
    handleSSEError(error, onError);
    if (!(error instanceof Error && error.name === 'AbortError')) {
      throw error;
    }
  } finally {
    onClose?.();
  }
}

/**
 * Transport adapter binding the generated @compass/client services to this app's
 * `apiClient`.
 *
 * Everything the dashboard already relies on is attached to `apiClient` itself — the
 * bearer-token interceptor, 401 refresh-and-replay, `axios-retry` for 429/5xx, error
 * toasts and logout redirects — so routing the generated services through it means they
 * inherit all of that unchanged.
 *
 * Two adaptations from raw axios:
 *   - The transport contract returns the **unwrapped body**, so each method maps
 *     `response.data`.
 *   - `stream()` yields payloads rather than `SSEEvent<T>` envelopes, so the `.data`
 *     unwrapping that the generated services used to do inline now happens here.
 */
/**
 * The dashboard implements every optional transport capability, so it is typed as such —
 * callers can use `transport.postForm(...)` / `transport.stream(...)` without a
 * possibly-undefined guard.
 */
type DashboardTransport = Transport & Required<Pick<Transport, 'postForm' | 'stream'>>;

export const transport: DashboardTransport = {
  // Method shorthand, not generic arrow functions: webpack runs esbuild with
  // `loader: 'tsx'` for every .ts file, where `<T>(...)` parses as JSX. The `<T,>`
  // disambiguation form works but `eslint --fix` strips the comma, so the build would
  // break again on the next format. Method shorthand is unambiguous in TSX.
  async get<T>(url: string, options?: RequestOptions) {
    return (await apiClient.get<T>(url, options as AxiosRequestConfig)).data;
  },

  async post<T>(url: string, body?: unknown, options?: RequestOptions) {
    return (await apiClient.post<T>(url, body, options as AxiosRequestConfig)).data;
  },

  async put<T>(url: string, body?: unknown, options?: RequestOptions) {
    return (await apiClient.put<T>(url, body, options as AxiosRequestConfig)).data;
  },

  async patch<T>(url: string, body?: unknown, options?: RequestOptions) {
    return (await apiClient.patch<T>(url, body, options as AxiosRequestConfig)).data;
  },

  // RequestOptions.data carries the body for the few DELETEs that need one
  // (e.g. RoleService.RemovePermissionFromRole).
  async delete<T>(url: string, options?: RequestOptions) {
    return (await apiClient.delete<T>(url, options as AxiosRequestConfig)).data;
  },

  // Deleting Content-Type lets axios set it with the multipart boundary.
  async postForm<T>(url: string, form: FormData, options?: RequestOptions) {
    return (
      await apiClient.post<T>(url, form, {
        ...(options as AxiosRequestConfig),
        transformRequest: [
          (data, headers) => {
            delete headers['Content-Type'];
            return data;
          },
        ],
      })
    ).data;
  },

  stream<T>(url: string, body: unknown, options?: StreamOptions): AsyncIterable<T> {
    const events = createSSEConnection<T>(url, (body ?? {}) as Record<string, unknown>, options);
    return {
      async *[Symbol.asyncIterator]() {
        for await (const event of events) {
          yield event.data;
        }
      },
    };
  },
};
